import React, { useCallback, useEffect, useState } from "react";
import {
  getStripeConnectStatus,
  getStripeDashboardLink,
  startStripeOnboarding,
  StripeConnectStatus,
} from "../api/stripe";

interface Props {
  /** Path Stripe should return to after onboarding, e.g. `/weddings/:id/manage?tab=stripe`. */
  returnPath?: string;
}

const StripeConnectPanel: React.FC<Props> = ({ returnPath }) => {
  const [stripeStatus, setStripeStatus] = useState<StripeConnectStatus | null>(null);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [stripeActionLoading, setStripeActionLoading] = useState(false);
  const [stripeError, setStripeError] = useState("");

  const loadStripeStatus = useCallback(async () => {
    setStripeLoading(true);
    setStripeError("");
    try {
      const { data } = await getStripeConnectStatus();
      setStripeStatus(data);
    } catch (err) {
      setStripeStatus(null);
      setStripeError(
        err instanceof Error ? err.message : "Failed to load Stripe status"
      );
    } finally {
      setStripeLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStripeStatus();
  }, [loadStripeStatus]);

  const handleConnectStripe = async () => {
    setStripeActionLoading(true);
    setStripeError("");
    try {
      const { data } = await startStripeOnboarding(
        returnPath ? { return_path: returnPath } : undefined
      );
      window.location.href = data.url;
    } catch (err) {
      setStripeError(
        err instanceof Error ? err.message : "Failed to start Stripe setup"
      );
      setStripeActionLoading(false);
    }
  };

  const handleManageStripe = async () => {
    setStripeActionLoading(true);
    setStripeError("");
    try {
      const { data } = await getStripeDashboardLink();
      window.location.href = data.url;
    } catch (err) {
      setStripeError(
        err instanceof Error ? err.message : "Failed to open Stripe dashboard"
      );
      setStripeActionLoading(false);
    }
  };

  const stripeReady = stripeStatus?.ready;
  const stripeNeedsSetup = stripeStatus?.connected && !stripeStatus?.ready;

  return (
    <div className="stripe-connect-panel profile-section">
      <h2 className="manage-section-title">Link Stripe Account</h2>
      <p className="form-hint">
        Connect your Stripe account to receive ticket payments for your weddings.
        Ticketed Wedding keeps a 3% platform fee; the rest goes to you. This is linked
        to your user account, so it applies to every wedding you manage.
      </p>

      {stripeError && <div className="error-message inline">{stripeError}</div>}

      {stripeLoading ? (
        <p className="form-hint">Loading Stripe status...</p>
      ) : (
        <>
          <div className="stripe-status-row">
            <span
              className={`status-badge ${
                stripeReady
                  ? "status-confirmed"
                  : stripeNeedsSetup
                    ? "status-pending"
                    : "status-declined"
              }`}
            >
              {stripeReady
                ? "Connected"
                : stripeNeedsSetup
                  ? "Setup incomplete"
                  : "Not connected"}
            </span>
          </div>

          <div className="button-row">
            {stripeReady ? (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={stripeActionLoading}
                onClick={handleManageStripe}
              >
                {stripeActionLoading ? "Opening..." : "Manage Stripe Dashboard"}
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={stripeActionLoading}
                onClick={handleConnectStripe}
              >
                {stripeActionLoading
                  ? "Redirecting..."
                  : stripeNeedsSetup
                    ? "Complete Stripe Setup"
                    : "Connect Stripe Account"}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default StripeConnectPanel;
